/* eslint-disable */
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { RiArrowRightSLine } from "react-icons/ri";
import map from "../../../assets2/images/map.png";
import NavbarCustomer from "../../../components/NavbarCustomer";
import VenueFilter from "../../../components/listing/filters/VenueFilter";
import VenueTab from "../../../components/listing/VenueTab";
import VenuStayTab from "../../../components/listing/VenuStayTab";
import VenueStayFilter from "../../../components/listing/filters/VenuStayFilter";
import {
	useLocation,
	useNavigate,
	useSearchParams,
	Link,
	useParams,
} from "react-router-dom";
import { base_URL } from "../../../config/config";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { deepClean } from "../../../utils/utilityFunctions";

const fetchHotels = async (page, sort, filters) => {
	try {
		const cleanedFilters = deepClean(filters || {});
		const { data } = await axios.get(`${base_URL}/api/hotels/get`, {
			params: { page, limit: 24, sort, ...cleanedFilters },
		});
		return data;
	} catch (error) {
		console.error("Error fetching hotels:", error);
		return { hotels: [], total: 0 };
	}
};
const Listing = ({
	filters,
	setFilters,
	showNav = true,
	tabNumber = "3",
	breadcrumbPath = [],
	areaCount = null,
}) => {
	const { tab } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const [totalHotels, setTotalHotels] = useState(0);
	const [totalVenues, setTotalVenues] = useState(0);
	const [activeTab, setActiveTab] = useState(tab || tabNumber || "3");
	const [isShowMap, setIsShowMap] = useState(false);
	const [page, setPage] = useState(1);
	const [sortValue, setSortValue] = useState("");
	const [count, setCount] = useState({ venue: "0", venueStay: "0" });

	const [params, setParams] = useSearchParams();
	const isSyncingRef = useRef(false); // Prevent circular updates
	const filterRef = useRef(null);

	/** 🔹 Generate query string from filters */
	const queryString = useMemo(() => {
		if (!filters || Object.keys(filters).length === 0) return "";
		const cleaned = deepClean(filters);
		if (Object.keys(cleaned).length === 0) return "";
		return `filters=${encodeURIComponent(JSON.stringify(cleaned))}`;
	}, [filters]);

	/** 🔹 Sync filters → URL (only when filters actually change) */
	useEffect(() => {
		if (isSyncingRef.current) return; // skip during URL → state sync

		const targetUrl = queryString
			? `${location.pathname}?${queryString}`
			: location.pathname;

		const currentUrl = `${location.pathname}${location.search}`;

		if (targetUrl !== currentUrl) {
			const handler = setTimeout(() => {
				navigate(targetUrl, { replace: false });
			}, 400);
			return () => clearTimeout(handler);
		}
	}, [queryString]);

	/** 🔹 Sync URL → filters (including browser back/forward) */
	useEffect(() => {
		isSyncingRef.current = true;
		const jsonString = params.get("filters");

		if (jsonString) {
			try {
				const parsed = JSON.parse(jsonString);
				setFilters(parsed);
			} catch (err) {
				console.error("Invalid filters in URL", err);
			}
		} else {
			// Only parse individual params if no JSON filter exists
			const parsedFilters = {};
			const city = params.get("city");
			const eventType = params.get("eventType");
			const hotelType = params.get("hotelType");
			const numberOfRooms = params.get("numberOfRooms");
			const standingGuests = params.get("standingGuests");
			const basePrice = params.get("basePrice");
			const search = params.get("search");

			if (city) parsedFilters.city = city;
			if (eventType) parsedFilters.eventType = eventType;
			if (hotelType) parsedFilters.hotelType = hotelType;
			if (numberOfRooms) parsedFilters.numberOfRooms = numberOfRooms;
			if (standingGuests) parsedFilters.standingGuests = standingGuests;
			if (basePrice) parsedFilters.basePrice = basePrice;
			if (search) parsedFilters.search = search;

			if (Object.keys(parsedFilters).length > 0) {
				setFilters(parsedFilters);
			}
		}

		setTimeout(() => (isSyncingRef.current = false), 100);
	}, [location.search]);

	/** 🔹 Fetch venue count (lightweight — does not pull rows) */
	const getVenuesCount = async () => {
		try {
			const response = await fetch(`${base_URL}/api/venues/count`);
			const data = await response.json();
			setCount((prev) => ({ ...prev, venue: data.total }));
		} catch (err) {
			console.log(err);
		}
	};
	const handleVenueCount = useCallback((value) => setTotalVenues(value), []);

	/** 🔹 Tabs definition — memoized so VenueTab/VenuStayTab don't remount on every Listing re-render */
	const tabs = useMemo(() => [
		{
			name: "Venue",
			id: "1",
			component: (
				<VenueTab
					filters={filters}
					setFilters={setFilters}
					setCount={handleVenueCount}
					count={count}
					isShowMap={isShowMap}
					setIsShowMap={setIsShowMap}
					breadcrumbPath={breadcrumbPath}
					page={page}
					setPage={setPage}
				/>
			),
		},
		{
			name: "Venue & Stay",
			id: "2",
			component: (
				<VenuStayTab
					filters={filters}
					setCount={setTotalHotels}
					totalHotels={totalHotels}
					count={count}
					isShowMap={isShowMap}
					setIsShowMap={setIsShowMap}
					breadcrumbPath={breadcrumbPath}
					page={page}
					setPage={setPage}
				/>
			),
		},
	], [filters, setFilters, handleVenueCount, count, isShowMap, breadcrumbPath, page, setPage, totalHotels]);

	useEffect(() => {
		if (tab === undefined || tab === "3") setActiveTab("1");
		else setActiveTab(tab);
	}, [tab]);

	const getVisibleTabs = () => {
		if (location.pathname.includes("event"))
			return tabs.filter((t) => t.id === "1");
		return tab === undefined || tab === "3"
			? tabs
			: tabs.filter((t) => t.id === activeTab);
	};

	return (
		<>
			{showNav && <NavbarCustomer filters={filters} setFilters={setFilters} />}

			<div className="bg-white sm:px-8 px-4 py-5">
				{showNav && (
					<div className="text-[#717171] flex items-center my-4 gap-2">
						<Link to="/">Home</Link>
						<RiArrowRightSLine />
						<p className="text-dark">Search Results</p>
					</div>
				)}

				<div className="flex items-start md:flex-row flex-col">
					{/* Sidebar */}
					<div
						ref={filterRef}
						className="lg:w-[25%] md:w-[35%] w-full md:border-r border-[#717171] md:pr-6 pb-6 md:pb-0 md:sticky md:top-0 md:self-start md:max-h-screen md:overflow-y-auto [&::-webkit-scrollbar]:hidden"
						style={{ scrollbarWidth: "none" }}
					>
						{!isShowMap && (
							<div className="flex justify-center items-center relative w-full">
								<img src={map} className="w-full h-full rounded-lg" alt="" />
								<button
									onClick={() => setIsShowMap(true)}
									className="bg-primary absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white sm:px-6 px-3 py-3 rounded-lg"
								>
									Show on Map
								</button>
							</div>
						)}
						{activeTab === "1" ? (
							<VenueFilter
								filters={filters}
								setFilters={setFilters}
								page={page}
								setPage={setPage}
							/>
						) : (
							<VenueStayFilter
								filters={filters}
								setFilters={setFilters}
								page={page}
								setPage={setPage}
							/>
						)}
					</div>

					{/* Right Section */}
					<div className="flex-1 md:pl-6 w-full">
						<div className="flex gap-7 xl:w-[70%] w-full">
							{getVisibleTabs()?.map((tab) => (
								<button
									key={tab.id}
									onClick={() => setActiveTab(tab.id)}
									disabled={tab.id === "3"}
									className={`w-fit px-6 py-2 ${activeTab === tab.id
										? "border-b-[3px] border-b-[#27C2B0] text-black font-semibold"
										: "border-b border-b-[#717171] text-[#717171]"
										}`}
								>
									{tab.name}{" "}
									{tab.name === "Venue" ? (
										<span className="text-[#FE4747]">({totalVenues})</span>
									) : tab.name === "Venue & Stay" ? (
										<span className="text-[#FE4747]">
											({areaCount?.hotels ?? totalHotels})
										</span>
									) : null}
								</button>
							))}
						</div>

						<div className="mt-4 w-full h-full bg-white">
							{activeTab === "3"
								? tabs.find((t) => t.id === "1")?.component
								: tabs.find((t) => t.id === activeTab)?.component}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Listing;
